import { generateEventDescriptions } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/common/utils';
import { createSecurityEventActionColumn } from '@dealstack/frontend/shared/components/Table/MantineTable/ColumnDefs/ButtonColumn';
import { createEventDate } from '@dealstack/frontend/shared/components/Table/MantineTable/ColumnDefs/DateColumn';
import { createNumberColumn } from '@dealstack/frontend/shared/components/Table/MantineTable/ColumnDefs/NumberColumn';
import { createTextColumn } from '@dealstack/frontend/shared/components/Table/MantineTable/ColumnDefs/TextColumn';
import { ExtendedColumnDef } from '@dealstack/frontend/shared/components/Table/MantineTable/useMantineTableWithPaginationFilteringSorting';
import { I18Namespaces } from '@dealstack/shared/lib/common/i18n/i18n.ts';
import {
	DATE_FORMATS,
	parseBEDateIgnoreTimezone,
} from '@dealstack/shared/lib/format/dates/date.ts';
import { normalizeScreamingSnakeToUI } from '@dealstack/shared/lib/format/string.ts';
import {
	DataSecurityTct,
	GetSecurityEventsRes,
	SecurityEventRes,
	SecurityEventType,
} from '@dealstack/shared/lib/generated';
import { Text } from '@mantine/core';
import { TranslateFunction } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.types';

export const getSecurityEventsTableColumns: (
	tCapManage: TranslateFunction<I18Namespaces.CAP_MANAGE>,
	setShowEditSecurityEventDrawer?: (value: boolean) => void,
	setSecurityEventEdit?: (transaction: SecurityEventRes) => void,
	security?: DataSecurityTct,
	securityEvents?: GetSecurityEventsRes,
	hasEditorPermission?: boolean
) => ExtendedColumnDef<SecurityEventRes>[] = (
	tCapManage: TranslateFunction<I18Namespaces.CAP_MANAGE>,
	setShowEditSecurityEventDrawer?: (value: boolean) => void,
	setSecurityEventEdit?: (transaction: SecurityEventRes) => void,
	security?: DataSecurityTct,
	securityEvents?: GetSecurityEventsRes,
	hasEditorPermission = true
) => {
	const eventDescriptions = generateEventDescriptions({
		security,
		securityEvents,
		tCapManage,
	});

	const baseColumns: ExtendedColumnDef<SecurityEventRes>[] = [
		createTextColumn({
			headerText: tCapManage('securities.table.columns.id'),
			accessorKey: 'id',
			enableGlobalFilter: true,
			filterValueType: 'uuid',
			filterText: tCapManage('securities.table.filter.id'),
			minSize: 360,
			size: 360,
		}),
		createEventDate({
			dateFormat: DATE_FORMATS.CALENDAR_DATE,
			headerText: tCapManage('securities.details.column_date'),
			accessorKey: 'date',
			sortingFn: 'datetime',
			accessorFn: (row) => {
				if (!row.date) {
					return null;
				}
				return parseBEDateIgnoreTimezone(row.date);
			},
			filterVariant: 'date-range',
			filterValueType: 'date-range',
			enableResizing: true,
		}),
		createTextColumn({
			headerText: tCapManage('securities.details.eventType'),
			accessorKey: 'type',
			enableGlobalFilter: false,
			enableSorting: false,
			accessorFn: (row) => normalizeScreamingSnakeToUI(row.type),
			filterVariant: 'multi-select',
			showFilter: true,
			filterText: tCapManage('securities.table.filter.type'),
			mantineFilterMultiSelectProps: {
				data: Object.values(SecurityEventType).map((type) => ({
					value: type,
					label: normalizeScreamingSnakeToUI(type),
				})),
			},
		}),
		createTextColumn({
			headerText: tCapManage('securities.details.column_description'),
			id: 'description',
			enableGlobalFilter: false,
			enableSorting: false,
			enableColumnFilter: false,
			accessorFn: (row) => row.type,
			cell: ({ cell }) => <Text size="B6Medium">{eventDescriptions[cell.row.id] ?? ''}</Text>,
			meta: {
				isGrow: true,
			},
		}),
		createNumberColumn({
			headerText: tCapManage('securities.details.pre_event_units'),
			accessorKey: 'preEventUnits',
			accessorFn: (row) => {
				return row.preEventUnits;
			},
		}),
		createNumberColumn({
			headerText: tCapManage('securities.details.post_event_units'),
			accessorKey: 'postEventUnits',
			accessorFn: (row) => {
				return row.postEventUnits;
			},
		}),
	];

	if (hasEditorPermission) {
		baseColumns.push(
			createSecurityEventActionColumn<SecurityEventRes>(
				setShowEditSecurityEventDrawer,
				setSecurityEventEdit
			)
		);
	}

	return baseColumns;
};
